import { useEffect, useRef, type RefObject } from "react";

export type GridIntensity = "subtle" | "visible";

interface GridIntensityConfig {
  subtle: { external: number; internal: number };
  visible: { external: number; internal: number };
}

const INTENSITY_CONFIG: GridIntensityConfig = {
  subtle: {
    external: 0.5,
    internal: 0.05,
  },
  visible: {
    external: 0.8,
    internal: 0.2,
  },
};

// Registry of all boundary elements and their intensities
const boundaries: Map<HTMLElement, GridIntensity> = new Map();
let currentIntensity: GridIntensity = "subtle";
let defaultIntensity: GridIntensity = "subtle";

/**
 * Sets CSS variables for grid line intensity on the document root.
 */
export function setGridIntensity(intensity: GridIntensity): void {
  if (intensity === currentIntensity) return;

  currentIntensity = intensity;
  const config = INTENSITY_CONFIG[intensity];
  document.documentElement.style.setProperty("--grid-external-opacity", config.external.toString());
  document.documentElement.style.setProperty("--grid-internal-opacity", config.internal.toString());
}

/**
 * Calculates which intensity should be active based on scroll position.
 * Finds the lowest boundary that has been scrolled past.
 */
function calculateActiveIntensity(): GridIntensity {
  let activeIntensity = defaultIntensity;
  let lowestPassedTop = -Infinity;

  boundaries.forEach((intensity, element) => {
    const rect = element.getBoundingClientRect();
    // If we've scrolled past this element (it's above viewport top)
    // and it's the lowest one we've passed, use its intensity
    if (rect.top <= 0 && rect.top > lowestPassedTop) {
      lowestPassedTop = rect.top;
      activeIntensity = intensity;
    }
  });

  return activeIntensity;
}

// Shared scroll handler
let scrollHandlerAttached = false;

function handleScroll(): void {
  const newIntensity = calculateActiveIntensity();
  setGridIntensity(newIntensity);
}

function attachScrollHandler(): void {
  if (scrollHandlerAttached) return;
  window.addEventListener("scroll", handleScroll, { passive: true });
  scrollHandlerAttached = true;
}

function detachScrollHandlerIfNeeded(): void {
  if (boundaries.size === 0 && scrollHandlerAttached) {
    window.removeEventListener("scroll", handleScroll);
    scrollHandlerAttached = false;
  }
}

interface UseGridIntensityZoneOptions {
  /** Intensity to apply when this section is the active zone */
  intensity: GridIntensity;
}

/**
 * Hook to mark a section as a grid intensity zone.
 * When this section's top edge passes the viewport top, its intensity becomes active.
 *
 * @example
 * ```tsx
 * function Problem() {
 *   const ref = useGridIntensityZone({ intensity: 'visible' });
 *   return <Section ref={ref}>...</Section>;
 * }
 * ```
 */
export function useGridIntensityZone<T extends HTMLElement = HTMLDivElement>(
  options: UseGridIntensityZoneOptions,
): RefObject<T | null> {
  const { intensity } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Register this boundary
    boundaries.set(element, intensity);
    attachScrollHandler();

    // Check position immediately
    handleScroll();

    return () => {
      boundaries.delete(element);
      detachScrollHandlerIfNeeded();
    };
  }, [intensity]);

  return ref;
}

/**
 * Hook to initialize grid intensity system.
 * Sets the default intensity (used when above all boundaries).
 */
export function useGridIntensityInit(initialIntensity: GridIntensity = "subtle"): void {
  useEffect(() => {
    defaultIntensity = initialIntensity;
    setGridIntensity(initialIntensity);
  }, [initialIntensity]);
}
